import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../../../environments/environment';
import { ConfigService } from '../../core/services/config/config.service';
import { PortalConfig } from '../models/portal-config';

// Minimal stand-in for the MSAL Logger returned by getLogger() in bypass mode.
interface NoopLogger {
  clone: () => NoopLogger;
  verbose: () => void;
  info: () => void;
  warning: () => void;
  error: () => void;
  trace: () => void;
  isPiiLoggingEnabled: () => boolean;
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  // MSAL previously logged every auth-flow event to the console in all
  // environments (visible in DevTools). Surface only genuine errors now; the
  // info/verbose chatter is suppressed (reinforced by logLevel: Error below).
  if (logLevel === LogLevel.Error) {
    console.error('[MSAL]', message);
  }
}

function resolveAppConfig(configService: ConfigService): PortalConfig {
  // Loosely typed: tolerates either the unwrapped config or a raw { portalConfig }.
  const config = (configService.config ?? {}) as PortalConfig & { portalConfig?: PortalConfig };
  return config.portalConfig ?? config;
}

export function MSALInstanceFactory(configService: ConfigService): IPublicClientApplication {
  const config = resolveAppConfig(configService);

  // In local auth bypass mode window.crypto.subtle is unavailable on plain-HTTP LAN
  // addresses, so PublicClientApplication cannot be constructed. Return a no-op stub;
  // AuthService.initialize() returns early in bypass mode so the instance is never used.
  if (environment.allowAuthBypass && config?.localAuthBypass) {
    const noopLogger: NoopLogger = {
      clone: () => noopLogger,
      verbose: () => {},
      info: () => {},
      warning: () => {},
      error: () => {},
      trace: () => {},
      isPiiLoggingEnabled: () => false,
    };
    return new Proxy({} as IPublicClientApplication, {
      get: (_t, prop) => {
        if (prop === 'initialize') return () => Promise.resolve();
        if (prop === 'handleRedirectPromise') return () => Promise.resolve(null);
        if (prop === 'getAllAccounts') return () => [];
        if (prop === 'getActiveAccount') return () => null;
        if (prop === 'getConfiguration') return () => ({});
        if (prop === 'enableAccountStorageEvents') return () => {};
        if (prop === 'disableAccountStorageEvents') return () => {};
        if (prop === 'addEventCallback') return () => '';
        if (prop === 'removeEventCallback') return () => {};
        if (prop === 'setActiveAccount') return () => {};
        if (prop === 'getLogger') return () => noopLogger;
        return () => Promise.resolve();
      },
    });
  }

  if (!config?.msalConfig?.auth) {
    throw new Error('MSAL auth configuration is missing. Verify config.json (msalConfig.auth).');
  }

  return new PublicClientApplication({
    auth: config.msalConfig.auth,
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: true,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(configService: ConfigService): MsalInterceptorConfiguration {
  const config = resolveAppConfig(configService);
  if (!config?.loginApiConfig?.endpoint || !config?.loginApiConfig?.scopes?.length) {
    throw new Error('MSAL configuration is missing. Verify config.json.');
  }

  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(config.loginApiConfig.endpoint, config.loginApiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(configService: ConfigService): MsalGuardConfiguration {
  const config = resolveAppConfig(configService);
  if (!config?.loginApiConfig?.scopes?.length || !config?.msalConfig?.auth?.redirectUri) {
    throw new Error('MSAL configuration is missing. Verify config.json.');
  }

  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...config.loginApiConfig.scopes],
      redirectUri: config.msalConfig.auth.redirectUri,
    },
    loginFailedRoute: '/unauthorized',
  };
}
